import { useState } from 'react';
import { mockDocuments } from '../../data/mockData.ts';
import { Document, DocumentStatus } from '../../types/document.ts';
import { ArrowLeft, Download, Edit, Clock, User, Tag, FileText, GitBranch, CheckCircle, XCircle, AlertCircle, IdCard } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

// interface DocumentDetailProps {
//   documentId: string;
//   onBack: () => void;
//   onEdit: (id: string) => void;
// }

export default function DocumentManageDetail() {
   const { id } = useParams(); // 来自 /documentsList/:id/show
  const navigate = useNavigate();


  const [document] = useState<Document | undefined>(
    mockDocuments.find(doc => doc.id === id)
  );

  if (!document) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">文档未找到</div>
      </div>
    );
  }

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Review':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'Obsolete':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleDownload = (fileUrl?: string, version?: string) => {
    if (fileUrl) {
      alert(`下载文件: ${document.documentNumber} v${version}\n文件: ${fileUrl}`);
    }
  };

  function onBack(event: React.MouseEvent<HTMLButtonElement>): void {
    navigate(-1);
  }

  function onEdit(id: string): void {
    // 跳转到编辑页面，例如 /documentsList/:id/edit
    navigate(`/documentsList/${id}/edit`);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          返回列表
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(document.status)}
              <h2 className="text-gray-900">{document.title}</h2>
            </div>
            <div className="text-gray-600 mb-2">{document.description}</div>
            <div className="flex items-center gap-4 text-gray-500">
              <span>文档编号: {document.documentNumber}</span>
              <span>•</span>
              <span>当前版本: v{document.currentVersion}</span>
              <span>•</span>
              <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                {document.status === 'Draft' && '草稿'}
                {document.status === 'Review' && '审批中'}
                {document.status === 'Approved' && '已批准'}
                {document.status === 'Obsolete' && '已废止'}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {document.status !== 'Obsolete' && (
              <button
                onClick={() => onEdit(document.id)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                编辑文档
              </button>
            )}
            <button
              onClick={() => handleDownload(
                document.versions.find(v => v.isActive)?.fileUrl,
                document.currentVersion
              )}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              下载当前版本
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-gray-900 mb-4">基本信息</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-gray-500 mb-1">类别</div>
                <div className="text-gray-900">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    {document.category}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">创建时间</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.createdAt).toLocaleString('zh-CN')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">创建人</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {document.createdBy}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">最后更新</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.updatedAt).toLocaleString('zh-CN')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">更新人</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {document.updatedBy}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">标签</div>
                <div className="flex flex-wrap gap-2">
                  {document.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Version History */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <GitBranch className="w-5 h-5 text-gray-700" />
              <h3 className="text-gray-900">版本历史</h3>
            </div>
            <div className="space-y-3">
              {document.versions.map((version, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    version.isActive
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-900">
                        版本 {version.version}
                      </span>
                      {version.isActive && (
                        <span className="px-2 py-0.5 bg-blue-600 text-white rounded">
                          当前版本
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded ${getStatusColor(version.status)}`}>
                        {version.status === 'Draft' && '草稿'}
                        {version.status === 'Review' && '审批中'}
                        {version.status === 'Approved' && '已批准'}
                        {version.status === 'Obsolete' && '已废止'}
                      </span>
                    </div>
                    {version.fileUrl && (
                      <button
                        onClick={() => handleDownload(version.fileUrl, version.version)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                      >
                        <Download className="w-4 h-4" />
                        下载
                      </button>
                    )}
                  </div>
                  <div className="text-gray-600 mb-2">{version.changes}</div>
                  <div className="flex items-center gap-4 text-gray-500">
                    <span>{new Date(version.createdAt).toLocaleString('zh-CN')}</span>
                    <span>•</span>
                    <span>{version.createdBy}</span>
                    {version.fileSize && (
                      <>
                        <span>•</span>
                        <span>{formatFileSize(version.fileSize)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval History */}
          {document.approvalHistory.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">审批历史</h3>
              <div className="space-y-3">
                {document.approvalHistory.map((record) => (
                  <div key={record.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-900">{record.approver}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-500">级别 {record.level}</span>
                        <span
                          className={`px-2 py-0.5 rounded ${
                            record.action === 'Approved'
                              ? 'bg-green-100 text-green-700'
                              : record.action === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {record.action === 'Approved' && '已批准'}
                          {record.action === 'Rejected' && '已驳回'}
                          {record.action === 'Pending' && '待审批'}
                        </span>
                      </div>
                      <span className="text-gray-500">
                        {new Date(record.timestamp).toLocaleString('zh-CN')}
                      </span>
                    </div>
                    {record.comments && (
                      <div className="text-gray-600">评论: {record.comments}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-gray-900 mb-3">文档状态</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">生命周期</span>
                <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                  {document.status === 'Draft' && '草稿'}
                  {document.status === 'Review' && '审批中'}
                  {document.status === 'Approved' && '已批准'}
                  {document.status === 'Obsolete' && '已废止'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">版本总数</span>
                <span className="text-gray-900">{document.versions.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">审批记录</span>
                <span className="text-gray-900">{document.approvalHistory.length}</span>
              </div>
            </div>
          </div>

          {/* Related Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-gray-900 mb-3">相关操作</h4>
            <div className="space-y-2">
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                查看完整审计日志
              </button>
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                导出文档信息
              </button>
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                打印文档
              </button>
              {document.status === 'Approved' && (
                <button className="w-full px-3 py-2 text-left text-red-600 hover:bg-red-50 rounded transition-colors">
                  标记为已废止
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
